import {
  Box,
  LuxDatePicker,
  LuxRadioButton,
  LuxSelect,
  LuxTextField,
  useMediaQuery,
} from '@gemini/fe-ds-core';
import useGetInsuranceConfiguration from '@hooks/useGetInsuranceConfiguration';
import useGetPatientPolicies, {
  PatientPolicyDTO,
} from '@hooks/useGetPatientPolicies';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { usePatientStore } from '@store/patientStore';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import { useEffect, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import {
  IInsuranceFormProps,
  InsuranceFormType,
} from './PatientDetailsInsuranceForm.interface';
import {
  addNewPolicy,
  drivenFieldsConversion,
  policiesDependenciesFields,
} from './PatientDetailsInsuranceForm.utils';
import { yupResolver } from '@hookform/resolvers/yup';
import { insuranceFormSchema } from './PatientDetailsInsuranceForm.schema';
import dayjs from 'dayjs';
import { FormIds } from '@utils/enums/FormIds.enum';
import { MobileBreakpoint } from '@utils/consts/breakpoints.consts';
import { useTranslation } from 'react-i18next';
import { LuxPhoneNumber } from '@gemini/lux-ds-pms';
import { getInsuranceFieldErrorProps } from '../InsurancesForm.utils';
import type { InsuranceFieldState } from '../InsurancesForm.utils';

const DEFAULT_PHONE_PREFIX = '+1';

const InsuranceForm = ({
  currentIndex,
  gender,
  countryStates,
  insuranceFormType,
  countries,
  holders,
  defaultInsuranceID,
  defaultInsuranceNameOther,
  onValidate,
}: IInsuranceFormProps) => {
  // #region Hooks

  const brandName = useOnlineBookingStore(state => state.brandName);
  const practiceId = useOnlineBookingStore(state => state.practiceId) || '';
  const infoUser = useOnlineBookingStore(state => state.infoUser);
  const updatePatient = usePatientStore(state => state.updatePatient);
  const isMobile = useMediaQuery(`(max-width:${MobileBreakpoint}px)`);
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  const [t] = useTranslation();
  const driveMap: ReturnType<typeof drivenFieldsConversion> =
    drivenFieldsConversion(formConfigs, insuranceFormType);
  const prefillablePatientId =
    currentIndex === 0 ? infoUser?.patientId : undefined;
  const validationMode = brandName ? 'onBlur' : 'onChange';
  const getFieldErrorProps = (fieldState: InsuranceFieldState) =>
    getInsuranceFieldErrorProps(fieldState, validationMode);

  const {
    control,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { isValid },
  } = useForm({
    mode: validationMode,
    reValidateMode: validationMode,
    resolver: yupResolver(
      insuranceFormSchema(
        insuranceFormType,
        !!formConfigs?.insuranceDetailsOptional,
        driveMap,
      ),
    ),
  });

  const organizationId = useOnlineBookingStore(
    state => state.generalInfo?.organizationId,
  );
  const provideInsuranceDetails = useWatch({
    control,
    name: `patientDetails.${insuranceFormType}.provideInsuranceDetails`,
  });

  const insuranceID = useWatch({
    control,
    name: `patientDetails.${insuranceFormType}.insuranceId`,
  });

  const policyID = useWatch({
    control,
    name: `patientDetails.${insuranceFormType}.insuranceMemberId`,
  });

  const isPrimaryHolder = useWatch({
    control,
    name: `patientDetails.${insuranceFormType}.isHolder`,
  });

  const patient = usePatientStore(state => state.patients[currentIndex]);

  const { data: policiesData, isLoading: isPoliciesLoading } =
    useGetPatientPolicies({
      organizationId: brandName ? practiceId : organizationId,
      patientId: prefillablePatientId,
      carrierId: insuranceID,
    });

  const { data: insurances, isLoading: isInsurancesLoading } =
    useGetInsuranceConfiguration({
      organizationId: brandName ? practiceId : organizationId,
      patientId: prefillablePatientId,
      onlyAvailable: !!brandName,
    });

  // #endregion Hooks

  // #region Functions

  const updatePoliciesDependencies = (newPolicy?: PatientPolicyDTO) => {
    if (!newPolicy) {
      setValue(`patientDetails.${insuranceFormType}.isHolder`, '', {
        shouldValidate: true,
      });
      policiesDependenciesFields.forEach(field => {
        if (field === 'phoneNumber') {
          setValue(
            `patientDetails.${insuranceFormType}.phone`,
            {
              number: '',
              prefix: DEFAULT_PHONE_PREFIX,
            },
            { shouldValidate: true },
          );
          return;
        }
        if (field === 'country') {
          return;
        }
        setValue(`patientDetails.${insuranceFormType}.${field}` as any, '', {
          shouldValidate: true,
        });
      });
      return;
    }
    const relationship = newPolicy.insuredPartyContact.relationship;
    const isHolderValue =
      !relationship ||
      relationship.toLowerCase() ===
        StaticValues.RELATIONSHIP_SELF.toLowerCase()
        ? StaticValues.YES
        : StaticValues.NO;
    setValue(`patientDetails.${insuranceFormType}.isHolder`, '', {
      shouldValidate: true,
    });

    if (isHolderValue === StaticValues.YES) {
      return;
    }

    Object.entries(newPolicy.insuredPartyContact).forEach(([key, value]) => {
      let valueToSet = value;
      const keyToSet = key;
      if (key === 'gender' || key === 'relationship') {
        valueToSet = value?.toLowerCase();
      }
      if (key === 'phoneNumber') {
        setValue(
          `patientDetails.${insuranceFormType}.phone`,
          {
            number: value?.phoneNumber || '',
            prefix: value?.prefix || DEFAULT_PHONE_PREFIX,
          },
          { shouldValidate: true },
        );
        return;
      }
      if (key !== 'sex') {
        setValue(
          `patientDetails.${insuranceFormType}.${keyToSet}` as any,
          valueToSet,
          { shouldValidate: true },
        );
      }
    });
  };

  // #endregion Functions

  // #region Effects

  useEffect(() => {
    // Impossible to prefill without patientId, so just add "Add New" options and that's it
    const isPolicyUnprefillable = !prefillablePatientId;

    if (!isPolicyUnprefillable && (isPoliciesLoading || !policiesData)) return;

    const currentMemberId = getValues(
      `patientDetails.${insuranceFormType}.insuranceMemberId`,
    );

    const isAlreadyValid =
      policiesData?.some(p => p.memberId === currentMemberId) ||
      currentMemberId === StaticValues.ADD_NEW;

    if (isAlreadyValid) {
      return;
    }

    const firstPolicy = isPolicyUnprefillable
      ? addNewPolicy
      : policiesData?.[0] || addNewPolicy;
    setValue(
      `patientDetails.${insuranceFormType}.insuranceMemberId`,
      firstPolicy?.memberId,
      {
        shouldValidate: true,
      },
    );
    updatePoliciesDependencies(firstPolicy);
  }, [
    insuranceID,
    prefillablePatientId,
    isPoliciesLoading,
    policiesData,
    insuranceFormType,
  ]);

  useEffect(() => {
    if (patient?.patientDetails || defaultInsuranceID) {
      const firstPrefillableInsuranceID = prefillablePatientId
        ? insurances?.[0]?.insuranceId?.toString()
        : '';
      const saved = patient?.patientDetails?.[insuranceFormType];
      const isEqual =
        insuranceFormType === InsuranceFormType.SECONDARY ||
        defaultInsuranceID === saved?.insuranceId;
      const savedInsuranceMemberId =
        insuranceFormType === InsuranceFormType.PRIMARY
          ? patient?.insurance?.memberId
          : patient?.insurance?.secondaryInsurance?.memberId;
      const insuranceMemberIdOther =
        saved?.insuranceMemberIdOther ||
        (saved?.insuranceMemberId === StaticValues.ADD_NEW &&
        savedInsuranceMemberId !== StaticValues.ADD_NEW
          ? savedInsuranceMemberId
          : '');
      const shouldValidateDetails =
        saved?.provideInsuranceDetails === StaticValues.YES ||
        !formConfigs?.insuranceDetailsOptional;
      const shouldDefaultIsHolder =
        shouldValidateDetails &&
        !driveMap?.isHolder?.hidden &&
        !driveMap?.isHolder?.optional &&
        !saved?.isHolder;
      const resetValues = {
        ...(isEqual ? saved : ({} as any)),
        insuranceId:
          defaultInsuranceID ||
          saved?.insuranceId ||
          firstPrefillableInsuranceID,
        insuranceNameOther: defaultInsuranceNameOther || saved?.other,
        insuranceMemberIdOther,
        phone: {
          number: saved?.phone?.number || '',
          prefix: saved?.phone?.prefix || DEFAULT_PHONE_PREFIX,
        },
        country: saved?.country || 'US',
        ...(shouldDefaultIsHolder ? { isHolder: '' } : {}),
      };

      console.log({ saved, insuranceFormType });

      reset({
        patientDetails: {
          [insuranceFormType]: resetValues,
        },
      });
      void trigger(`patientDetails.${insuranceFormType}` as const);
    }
    return () => {
      const values = getValues();
      updatePatient(
        currentIndex,
        insuranceFormType === InsuranceFormType.PRIMARY
          ? FormIds.PatientDetailPrimaryInsurance
          : FormIds.PatientDetailSecondaryInsurance,
        {
          ...values,
        },
        insurances,
      );
    };
  }, [
    defaultInsuranceID,
    defaultInsuranceNameOther,
    currentIndex,
    prefillablePatientId,
    insurances,
    insuranceFormType,
    formConfigs?.insuranceDetailsOptional,
    driveMap?.isHolder?.hidden,
    driveMap?.isHolder?.optional,
    trigger,
  ]);

  useEffect(() => {
    onValidate(isValid, currentIndex);
  }, [isValid, currentIndex]);
  // #endregion Effects

  // #region Memos

  const toggleYesNoList = useMemo(
    () => [
      { label: 'Yes', value: StaticValues.YES },
      { label: 'No', value: StaticValues.NO },
    ],
    [],
  );

  const insuranceList = useMemo(() => {
    if (!insurances || isInsurancesLoading) return [];

    return insurances.map(ins => ({
      label: ins.name,
      value: ins.insuranceId?.toString(),
      isBold: ins.hasPolicy,
    }));
  }, [insurances, isInsurancesLoading]);

  const policiesList = useMemo(() => {
    return (policiesData || []).concat(addNewPolicy)?.map(policy => ({
      label: policy.memberId,
      value: policy.memberId?.toString(),
    }));
  }, [addNewPolicy, policiesData, isPoliciesLoading]);

  const genderList = useMemo(() => {
    if (!gender) return [];

    return gender.map(g => ({
      label: g.itemName,
      value: g.itemId,
    }));
  }, [gender]);

  const holdersList = useMemo(() => {
    if (!holders) return [];
    return holders
      .filter(h => {
        if (isPrimaryHolder === StaticValues.NO) {
          return h.itemId.toLowerCase() !== 'self';
        }
        return true;
      })
      .map(h => ({
        label: h.itemName,
        value: h.itemId,
      }));
  }, [holders, isPrimaryHolder]);

  const countryStatesList = useMemo(() => {
    if (!countryStates) return [];
    return countryStates.map(c => ({
      label: c.name,
      value: c.code,
    }));
  }, [countryStates]);

  const countriesList = useMemo(() => {
    if (!countries) return [];
    return countries.map(c => ({
      label: c.name,
      value: c.code,
    }));
  }, [countries]);

  // #endregion Memos

  // #region Constants

  const PROVIDE_DETAILS_IS_YES = provideInsuranceDetails === StaticValues.YES;
  const PROVIDE_DETAILS_IS_OPTIONAL = formConfigs?.insuranceDetailsOptional;
  const RENDER_SUBMIT_DEPENDENCIES =
    PROVIDE_DETAILS_IS_YES || !PROVIDE_DETAILS_IS_OPTIONAL;
  const PRIMARY_IS_OTHER = insuranceID === StaticValues.OTHER_OPTION;
  const CAN_SELECT_PATIENT_POLICY = !!prefillablePatientId;
  const POLICIES_IS_CUSTOM = policyID === StaticValues.ADD_NEW;
  const SHOW_MEMBER_ID_TEXT_FIELD =
    !CAN_SELECT_PATIENT_POLICY || POLICIES_IS_CUSTOM;
  const PRIMARY_HOLDER_FIELDS = isPrimaryHolder === StaticValues.NO;
  const DISABLE_INSURANCE_ID = !!(
    formConfigs?.insuranceMustDriveAvailability &&
    patient?.insurance?.id &&
    insuranceFormType === InsuranceFormType.PRIMARY
  );
  const DISABLE_OTHER_INSURANCE_ID = !!(
    formConfigs?.insuranceMustDriveAvailability &&
    patient?.insurance?.id &&
    insuranceFormType === InsuranceFormType.PRIMARY
  );
  const IS_MOBILE = isMobile;
  // #endregion Constants

  // #region JSX

  return (
    <Box padding={'0.5rem 1rem 1rem 1rem'}>
      <Box
        display="grid"
        gridTemplateColumns={IS_MOBILE ? '1fr' : '1fr 1fr'}
        gap="1rem"
      >
        {!driveMap?.insuranceId?.hidden && (
          <>
            <Controller
              name={`patientDetails.${insuranceFormType}.insuranceId`}
              control={control}
              render={({ field, fieldState }) => {
                console.log({ insuranceValue: field.value });
                return (
                  <LuxSelect
                    label={
                      t(
                        `fieldsLabel.${insuranceFormType}.insuranceId`,
                      ) as string
                    }
                    customOptions={insuranceList}
                    usePropsOptions
                    required={!driveMap?.insuranceId?.optional}
                    value={field.value || ''}
                    {...getFieldErrorProps(fieldState)}
                    onChange={e => {
                      if (
                        e.target.value ===
                        getValues(
                          `patientDetails.${insuranceFormType}.insuranceId`,
                        )
                      ) {
                        return;
                      }
                      field.onChange(e);
                      setValue(
                        `patientDetails.${insuranceFormType}.insuranceMemberId`,
                        '',
                        {
                          shouldDirty: true,
                          shouldValidate: true,
                        },
                      );
                    }}
                    disabled={DISABLE_INSURANCE_ID}
                    /* {...field} */
                  />
                );
              }}
            />

            {PRIMARY_IS_OTHER && (
              <Controller
                name={`patientDetails.${insuranceFormType}.insuranceNameOther`}
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <LuxTextField
                      label={
                        t(`fieldsLabel.${insuranceFormType}.other`) as string
                      }
                      disabled={DISABLE_OTHER_INSURANCE_ID}
                      {...field}
                      value={field.value || ''}
                      required
                      maxLength={255}
                      {...getFieldErrorProps(fieldState)}
                    />
                  );
                }}
              />
            )}
          </>
        )}
        {!driveMap?.insuranceMemberId?.hidden && (
          <>
            {CAN_SELECT_PATIENT_POLICY && (
              <Controller
                name={`patientDetails.${insuranceFormType}.insuranceMemberId`}
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <LuxSelect
                      label={
                        t(
                          `fieldsLabel.${insuranceFormType}.insuranceMemberId`,
                        ) as string
                      }
                      customOptions={policiesList as unknown as any}
                      usePropsOptions
                      emptyLabel="No options"
                      required={!driveMap?.insuranceMemberId?.optional}
                      value={field.value || ''}
                      {...getFieldErrorProps(fieldState)}
                      onChange={e => {
                        field.onChange(e);
                        const selectedPolicy = policiesData?.find(
                          p => p.memberId === (e as any)?.target?.value,
                        );
                        updatePoliciesDependencies(selectedPolicy);
                      }}
                    />
                  );
                }}
              />
            )}

            {SHOW_MEMBER_ID_TEXT_FIELD && (
              <Controller
                name={`patientDetails.${insuranceFormType}.insuranceMemberIdOther`}
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <LuxTextField
                      label={
                        t(
                          CAN_SELECT_PATIENT_POLICY
                            ? `fieldsLabel.${insuranceFormType}.insuranceMemberIdOther`
                            : `fieldsLabel.${insuranceFormType}.insuranceMemberId`,
                        ) as string
                      }
                      required={!driveMap?.insuranceMemberId?.optional}
                      {...getFieldErrorProps(fieldState)}
                      {...field}
                      value={field.value || ''}
                    />
                  );
                }}
              />
            )}
          </>
        )}
      </Box>
      {PROVIDE_DETAILS_IS_OPTIONAL && (
        <Box marginTop="1rem">
          <Controller
            name={`patientDetails.${insuranceFormType}.provideInsuranceDetails`}
            control={control}
            render={({ field }) => {
              return (
                <LuxRadioButton
                  {...field}
                  name={`${insuranceFormType}_provideInsuranceDetails_${currentIndex}`}
                  direction="horizontal"
                  required
                  standalone={true}
                  label={
                    t(
                      `fieldsLabel.${insuranceFormType}.provideInsuranceDetails`,
                    ) as string
                  }
                  onChange={value => {
                    const insuranceDetailsPath =
                      `patientDetails.${insuranceFormType}` as const;
                    const isHolderPath =
                      `${insuranceDetailsPath}.isHolder` as const;
                    const shouldValidateDetails =
                      value === StaticValues.YES ||
                      !formConfigs?.insuranceDetailsOptional;
                    setValue(
                      `patientDetails.${insuranceFormType}.provideInsuranceDetails`,
                      value,
                      {
                        shouldDirty: true,
                        shouldValidate: true,
                      },
                    );
                    if (
                      shouldValidateDetails &&
                      !driveMap?.isHolder?.hidden &&
                      !driveMap?.isHolder?.optional
                    ) {
                      setValue(isHolderPath, '', {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }
                    void trigger(insuranceDetailsPath);
                  }}
                  options={toggleYesNoList}
                />
              );
            }}
          />
        </Box>
      )}
      {RENDER_SUBMIT_DEPENDENCIES && (
        <>
          <Box
            display="grid"
            gridTemplateColumns={'1fr'}
            gap="1rem"
            marginTop="1rem"
          >
            {!driveMap?.isHolder?.hidden && (
              <Controller
                name={`patientDetails.${insuranceFormType}.isHolder`}
                control={control}
                render={({ field }) => {
                  return (
                    <LuxRadioButton
                      name={`${insuranceFormType}_isHolder_${currentIndex}`}
                      direction="horizontal"
                      label={
                        t(`fieldsLabel.${insuranceFormType}.isHolder`) as string
                      }
                      options={toggleYesNoList}
                      required={!driveMap?.isHolder?.optional}
                      standalone={true}
                      value={field.value || ''}
                      onChange={value => {
                        setValue(
                          `patientDetails.${insuranceFormType}.isHolder`,
                          value,
                          {
                            shouldDirty: true,
                            shouldValidate: true,
                          },
                        );
                      }}
                    />
                  );
                }}
              />
            )}
          </Box>

          {PRIMARY_HOLDER_FIELDS && !driveMap?.firstName?.hidden && (
            <>
              <Box
                display="grid"
                gridTemplateColumns={IS_MOBILE ? '1fr' : '1fr 1fr'}
                gap="1rem"
              >
                {!driveMap?.firstName?.hidden && (
                  <Controller
                    name={`patientDetails.${insuranceFormType}.firstName`}
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <LuxTextField
                          label={t('fieldsLabel.firstName') as string}
                          {...field}
                          {...getFieldErrorProps(fieldState)}
                          required={!driveMap?.firstName?.optional}
                        />
                      );
                    }}
                  />
                )}
                {!driveMap?.lastName?.hidden && (
                  <Controller
                    name={`patientDetails.${insuranceFormType}.lastName`}
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <LuxTextField
                          label={t('fieldsLabel.lastName') as string}
                          {...field}
                          required={!driveMap?.lastName?.optional}
                          {...getFieldErrorProps(fieldState)}
                        />
                      );
                    }}
                  />
                )}
                {!driveMap?.gender?.hidden && (
                  <Controller
                    name={`patientDetails.${insuranceFormType}.gender`}
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <LuxSelect
                          {...field}
                          label={t('fieldsLabel.gender') as string}
                          required={!driveMap?.gender?.optional}
                          value={field.value || ''}
                          {...getFieldErrorProps(fieldState)}
                          customOptions={genderList}
                          usePropsOptions
                        />
                      );
                    }}
                  />
                )}
                {!driveMap?.dateOfBirth.hidden && (
                  <Controller
                    name={`patientDetails.${insuranceFormType}.dateOfBirth`}
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <LuxDatePicker
                          label={t('fieldsLabel.dob') as string}
                          {...field}
                          required={!driveMap?.dateOfBirth?.optional}
                          isMonthPicker
                          pickDay
                          fullWidth
                          disableFuture
                          value={field.value ? dayjs(field.value) : null}
                          format="DD/MM/YYYY"
                          {...getFieldErrorProps(fieldState)}
                        />
                      );
                    }}
                  />
                )}
              </Box>
              <Box
                display="grid"
                gridTemplateColumns={IS_MOBILE ? '1fr' : 'repeat(12, 1fr)'}
                gap="1rem"
              >
                {!driveMap?.phone?.hidden && (
                  <Box gridColumn={IS_MOBILE ? '1 / -1' : 'span 8'}>
                    <Controller
                      name={`patientDetails.${insuranceFormType}.phone`}
                      control={control}
                      render={({ field, fieldState }) => {
                        const phoneValue = field.value || {};
                        const phonePrefix =
                          phoneValue.prefix || DEFAULT_PHONE_PREFIX;

                        return (
                          <LuxPhoneNumber
                            value={phoneValue.number || ''}
                            required={!driveMap?.phone?.optional}
                            label={t(`fieldsLabel.phoneNumber`) as string}
                            defaultValue={phoneValue.number || ''}
                            defaultValuePrefix={phonePrefix}
                            defaultCountry="us"
                            setPrefixValue={prefix => {
                              if (prefix === phonePrefix) {
                                return;
                              }

                              setValue(
                                `patientDetails.${insuranceFormType}.phone.prefix`,
                                prefix,
                              );
                            }}
                            onChange={number => {
                              if (number === phoneValue.number) {
                                return;
                              }

                              field.onChange({
                                ...phoneValue,
                                prefix: phonePrefix,
                                number,
                              });
                            }}
                            onBlur={field.onBlur}
                            {...getFieldErrorProps(fieldState)}
                          />
                        );
                      }}
                    />
                  </Box>
                )}
              </Box>
              <Box
                display="grid"
                gridTemplateColumns={IS_MOBILE ? '1fr' : '1fr 1fr'}
                gap="1rem"
              >
                {!driveMap?.streetAddress?.hidden && (
                  <Controller
                    name={`patientDetails.${insuranceFormType}.streetAddress`}
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <LuxTextField
                          label={t(`fieldsLabel.streetAddress`) as string}
                          {...field}
                          required={!driveMap?.streetAddress?.optional}
                          {...getFieldErrorProps(fieldState)}
                        />
                      );
                    }}
                  />
                )}
                {!driveMap?.city?.hidden && (
                  <Controller
                    name={`patientDetails.${insuranceFormType}.city`}
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <LuxTextField
                          label={t(`fieldsLabel.city`) as string}
                          {...field}
                          required={!driveMap?.city?.optional}
                          {...getFieldErrorProps(fieldState)}
                        />
                      );
                    }}
                  />
                )}
              </Box>
              <Box
                display="grid"
                gridTemplateColumns={IS_MOBILE ? '1fr 1fr' : '0.5fr 0.5fr 1fr'}
                gap="1rem"
              >
                {!driveMap?.zipCode?.hidden && (
                  <Controller
                    name={`patientDetails.${insuranceFormType}.zipCode`}
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <LuxTextField
                          label={t(`fieldsLabel.zipCode`) as string}
                          {...field}
                          required={!driveMap?.zipCode?.optional}
                          {...getFieldErrorProps(fieldState)}
                        />
                      );
                    }}
                  />
                )}
                {!driveMap?.state?.hidden && (
                  <Controller
                    name={`patientDetails.${insuranceFormType}.state`}
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <LuxSelect
                          {...field}
                          label={t(`fieldsLabel.state`) as string}
                          required={!driveMap?.state?.optional}
                          value={field.value || ''}
                          {...getFieldErrorProps(fieldState)}
                          customOptions={countryStatesList}
                          usePropsOptions
                        />
                      );
                    }}
                  />
                )}
                {!driveMap?.country?.hidden && (
                  <Controller
                    name={`patientDetails.${insuranceFormType}.country`}
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <LuxSelect
                          {...field}
                          label={t(`fieldsLabel.country`) as string}
                          required={!driveMap?.country?.optional}
                          value={'US'}
                          disabled
                          {...getFieldErrorProps(fieldState)}
                          customOptions={countriesList}
                          usePropsOptions
                        />
                      );
                    }}
                  />
                )}
              </Box>
              <Box
                display="grid"
                gridTemplateColumns={IS_MOBILE ? '1fr' : '0.5fr'}
                gap="1rem"
              >
                {!driveMap?.relationship?.hidden && (
                  <Controller
                    name={`patientDetails.${insuranceFormType}.relationship`}
                    control={control}
                    render={({ field, fieldState }) => {
                      return (
                        <LuxSelect
                          label={
                            t(
                              `fieldsLabel.${insuranceFormType}.relationship`,
                            ) as string
                          }
                          {...field}
                          required={!driveMap?.relationship?.optional}
                          {...getFieldErrorProps(fieldState)}
                          customOptions={holdersList}
                          usePropsOptions
                        />
                      );
                    }}
                  />
                )}
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
  // #endregion JSX
};

export default InsuranceForm;
